import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    GoogleMap,
    useJsApiLoader,
} from "@react-google-maps/api";

import Globals from "../../utils/globals";


const StatsHeatMapSection = ({ points, title }) => {
    const mapRef = useRef(null);
    const overlayRef = useRef(null);
    const [map, setMap] = useState(null);

    const { isLoaded, loadError } =
        useJsApiLoader({
            googleMapsApiKey:
                Globals.googleMapsApiKey,
        });


    // -------------------------------------------------------------
    // CLEAN GPS DATA
    //
    // Ignore records that do not contain valid latitude/longitude.
    // The backend already limits this to real incident reports.
    // -------------------------------------------------------------
    const validPoints = useMemo(() => {
        if (!Array.isArray(points)) {
            return [];
        }

        return points.filter((point) => {
            const latitude =
                Number(point?.latitude);

            const longitude =
                Number(point?.longitude);

            return (
                Number.isFinite(latitude) &&
                Number.isFinite(longitude) &&
                latitude >= -90 &&
                latitude <= 90 &&
                longitude >= -180 &&
                longitude <= 180
            );
        });
    }, [points]);


    // -------------------------------------------------------------
    // BUILD OUR OWN GOOGLE MAPS HEAT OVERLAY
    //
    // Google retired its visualization.HeatmapLayer.
    // This custom OverlayView keeps Google Maps as the basemap while
    // Transit Watch renders the incident-density layer itself.
    // -------------------------------------------------------------
    useEffect(() => {
        if (
            !isLoaded ||
            !map ||
            !window.google ||
            validPoints.length === 0
        ) {
            return;
        }

        // Remove an old overlay before creating a new one.
        if (overlayRef.current) {
            overlayRef.current.setMap(null);
            overlayRef.current = null;
        }

        class TransitWatchHeatOverlay
            extends window.google.maps.OverlayView {

            constructor(map, heatPoints) {
                super();

                this.heatPoints = heatPoints;
                this.canvas = null;

                this.setMap(map);
            }


            onAdd() {
                this.canvas =
                    document.createElement("canvas");

                this.canvas.style.position =
                    "absolute";

                this.canvas.style.pointerEvents =
                    "none";

                const panes =
                    this.getPanes();

                panes.overlayLayer.appendChild(
                    this.canvas
                );
            }


            draw() {
                if (!this.canvas) {
                    return;
                }

                const map =
                    this.getMap();

                const projection =
                    this.getProjection();

                const bounds =
                    map.getBounds();

                if (
                    !projection ||
                    !bounds
                ) {
                    return;
                }

                const northEast =
                    projection.fromLatLngToDivPixel(
                        bounds.getNorthEast()
                    );

                const southWest =
                    projection.fromLatLngToDivPixel(
                        bounds.getSouthWest()
                    );

                const width =
                    Math.max(
                        1,
                        Math.round(
                            northEast.x -
                            southWest.x
                        )
                    );

                const height =
                    Math.max(
                        1,
                        Math.round(
                            southWest.y -
                            northEast.y
                        )
                    );

                this.canvas.width = width;
                this.canvas.height = height;

                this.canvas.style.left =
                    `${southWest.x}px`;

                this.canvas.style.top =
                    `${northEast.y}px`;

                this.canvas.style.width =
                    `${width}px`;

                this.canvas.style.height =
                    `${height}px`;

                const context =
                    this.canvas.getContext("2d");

                context.clearRect(
                    0,
                    0,
                    width,
                    height
                );


                // -------------------------------------------------
                // FIRST PASS
                //
                // Draw soft grayscale density circles. Points that
                // overlap naturally become more intense.
                // -------------------------------------------------
                this.heatPoints.forEach(
                    (point) => {
                        const latLng =
                            new window.google.maps.LatLng(
                                Number(
                                    point.latitude
                                ),
                                Number(
                                    point.longitude
                                )
                            );

                        const pixel =
                            projection
                                .fromLatLngToDivPixel(
                                    latLng
                                );

                        const x =
                            pixel.x -
                            southWest.x;

                        const y =
                            pixel.y -
                            northEast.y;

                        // Radius grows slightly as the user zooms in.
                        const zoom =
                            map.getZoom() || 10;

                        const radius =
                            Math.max(
                                24,
                                Math.min(
                                    55,
                                    24 +
                                    (zoom - 9) * 4
                                )
                            );

                        const gradient =
                            context
                                .createRadialGradient(
                                    x,
                                    y,
                                    0,
                                    x,
                                    y,
                                    radius
                                );

                        gradient.addColorStop(
                            0,
                            "rgba(0,0,0,0.30)"
                        );

                        gradient.addColorStop(
                            0.35,
                            "rgba(0,0,0,0.20)"
                        );

                        gradient.addColorStop(
                            0.7,
                            "rgba(0,0,0,0.08)"
                        );

                        gradient.addColorStop(
                            1,
                            "rgba(0,0,0,0)"
                        );

                        context.fillStyle =
                            gradient;

                        context.fillRect(
                            x - radius,
                            y - radius,
                            radius * 2,
                            radius * 2
                        );
                    }
                );


                // -------------------------------------------------
                // SECOND PASS — COLORIZE DENSITY
                //
                // Convert grayscale intensity into the familiar
                // heat-map progression:
                //
                // blue -> cyan -> green -> yellow -> red
                // -------------------------------------------------
                const image =
                    context.getImageData(
                        0,
                        0,
                        width,
                        height
                    );

                const pixels =
                    image.data;

                for (
                    let i = 0;
                    i < pixels.length;
                    i += 4
                ) {
                    const alpha =
                        pixels[i + 3];

                    if (alpha === 0) {
                        continue;
                    }

                    const intensity =
                        Math.min(
                            1,
                            alpha / 180
                        );

                    let red;
                    let green;
                    let blue;


                    if (intensity < 0.25) {
                        // Blue -> Cyan
                        const t =
                            intensity / 0.25;

                        red = 0;
                        green =
                            Math.round(
                                140 +
                                115 * t
                            );

                        blue = 255;

                    } else if (
                        intensity < 0.5
                    ) {
                        // Cyan -> Green
                        const t =
                            (intensity - 0.25) /
                            0.25;

                        red = 0;
                        green = 255;
                        blue =
                            Math.round(
                                255 *
                                (1 - t)
                            );

                    } else if (
                        intensity < 0.75
                    ) {
                        // Green -> Yellow
                        const t =
                            (intensity - 0.5) /
                            0.25;

                        red =
                            Math.round(
                                255 * t
                            );

                        green = 255;
                        blue = 0;

                    } else {
                        // Yellow -> Red
                        const t =
                            (intensity - 0.75) /
                            0.25;

                        red = 255;
                        green =
                            Math.round(
                                255 *
                                (1 - t)
                            );

                        blue = 0;
                    }

                    pixels[i] = red;
                    pixels[i + 1] = green;
                    pixels[i + 2] = blue;

                    // Keep the basemap readable beneath the heat.
                    pixels[i + 3] =
                        Math.min(
                            190,
                            alpha
                        );
                }

                context.putImageData(
                    image,
                    0,
                    0
                );
            }


            onRemove() {
                if (
                    this.canvas &&
                    this.canvas.parentNode
                ) {
                    this.canvas.parentNode
                        .removeChild(
                            this.canvas
                        );
                }

                this.canvas = null;
            }
        }


            overlayRef.current =
              new TransitWatchHeatOverlay(
                 map,
                 validPoints
              );


        return () => {
            if (overlayRef.current) {
                overlayRef.current.setMap(
                    null
                );

                overlayRef.current = null;
            }
        };

       }, [isLoaded, map, validPoints]);


    // -------------------------------------------------------------
    // FIT MAP TO INCIDENT LOCATIONS
    //
    // Allows the same map to work automatically for every agency.
    // -------------------------------------------------------------
    const handleMapLoad = (map) => {
        mapRef.current = map;
        setMap(map);

        if (
            validPoints.length === 0 ||
            !window.google
        ) {
            return;
        }

        const bounds =
            new window.google.maps.LatLngBounds();

        validPoints.forEach((point) => {
            bounds.extend({
                lat: Number(
                    point.latitude
                ),
                lng: Number(
                    point.longitude
                ),
            });
        });

        map.fitBounds(bounds);

        if (validPoints.length === 1) {
            map.setZoom(15);
        }
    };


    if (loadError) {
        return (
            <div className="alert alert-danger">
                Unable to load the Google Map.
            </div>
        );
    }


    if (!isLoaded) {
        return (
            <div>
                Loading Heat Map...
            </div>
        );
    }


    if (validPoints.length === 0) {
        return (
            <div
                className="alert alert-secondary"
                style={{ marginTop: "20px" }}
            >
                No GPS coordinates are available for the
                selected reporting period and filters.
            </div>
        );
    }


    return (
        <div
            style={{
                marginTop: "30px",
                marginBottom: "30px",
            }}
        >
            <div
                data-pdf-heatmap
                style={{
                    background: "#fff",
                    border: "1px solid #cfd4da",
                    borderRadius: "8px",
                    padding: "20px",
                }}
            >
                <h3
                    style={{
                        marginBottom: "4px",
                    }}
                >
                    {title || "Incident Location Heat Map"}
                </h3>

                <div
                    style={{
                        color: "#666",
                        marginBottom: "16px",
                    }}
                >
                    Higher-intensity areas indicate greater
                    concentrations of reported incidents.
                </div>

                <div
                    style={{
                        marginBottom: "12px",
                        fontWeight: 600,
                    }}
                >
                    Incidents with GPS:{" "}
                    {validPoints.length}
                </div>

                <GoogleMap
                    mapContainerStyle={{
                        width: "100%",
                        height: "650px",
                    }}
                    onLoad={handleMapLoad}
                    options={{
                        gestureHandling: "greedy",
                        zoomControl: true,
                        scrollwheel: true,
                        streetViewControl: false,
                        mapTypeControl: true,
                        fullscreenControl: true,
                    }}
                />
            </div>
        </div>
    );
};

const StatsHeatMap = ({
    points,
    groups,
    period,
}) => {
    const validGroups =
        Array.isArray(groups)
            ? groups.filter(
                (group) =>
                    Array.isArray(group?.points) &&
                    group.points.length > 0
            )
            : [];

    // Backward compatibility with the existing flat heatmap_points
    // response while grouped Heat Maps are being rolled out.
    if (validGroups.length === 0) {
        return (
            <StatsHeatMapSection
                points={points}
                title="Incident Location Heat Map"
            />
        );
    }

    const getGroupTitle = (group) => {
        if (period === "day") {
            return `Day ${group.label} — Incident Location Heat Map`;
        }

        if (period === "month") {
            const monthNames = {
                Jan: "January",
                Feb: "February",
                Mar: "March",
                Apr: "April",
                May: "May",
                Jun: "June",
                Jul: "July",
                Aug: "August",
                Sep: "September",
                Oct: "October",
                Nov: "November",
                Dec: "December",
            };

            return `${monthNames[group.label] || group.label} — Incident Location Heat Map`;
        }

        if (period === "quarter") {
            return `${group.label} — Incident Location Heat Map`;
        }

        if (period === "year") {
            return `${group.label} — Incident Location Heat Map`;
        }

        return `${group.label} — Incident Location Heat Map`;
    };

    return (
        <div>
            {validGroups.map((group) => (
                <StatsHeatMapSection
                    key={group.key}
                    points={group.points}
                    title={getGroupTitle(group)}
                />
            ))}
        </div>
    );
};

export default StatsHeatMap;